import type { FFmpeg } from '@ffmpeg/ffmpeg';
import { rememberFfmpegLog, updateEncodeProgressFromLog } from '../progress';
import type { Runtime } from '../types';

let runtimePromise: Promise<Runtime> | null = null;

export function loadFFmpeg(): Promise<Runtime> {
  if (runtimePromise) {
    return runtimePromise;
  }

  const pendingRuntime = (async () => {
    const [{ FFmpeg }, { fetchFile: readFile, toBlobURL }] = await Promise.all([import('@ffmpeg/ffmpeg'), import('@ffmpeg/util')]);
    const ffmpeg = new FFmpeg();

    ffmpeg.on('log', ({ message }) => {
      const cleanMessage = message.trim();
      if (cleanMessage && cleanMessage !== 'Aborted()') {
        rememberFfmpegLog(cleanMessage);
        updateEncodeProgressFromLog(cleanMessage);
      }
    });

    const [coreURL, wasmURL] = await Promise.all([
      toBlobURL('/ffmpeg-core/ffmpeg-core.js', 'text/javascript'),
      toFfmpegWasmBlobURL(getFfmpegWasmUrl()),
    ]);

    await ffmpeg.load({ coreURL, wasmURL });

    return { ffmpeg, fetchFile: readFile };
  })();

  runtimePromise = pendingRuntime;
  pendingRuntime.catch(() => {
    // Forget failed loads so the next generate attempt can retry.
    if (runtimePromise === pendingRuntime) {
      runtimePromise = null;
    }
  });

  return pendingRuntime;
}

export function getFfmpegWasmUrl() {
  return import.meta.env.DEV ? '/ffmpeg-core/ffmpeg-core.wasm' : '/ffmpeg-core/ffmpeg-core.wasm.gz';
}

export async function cleanupVirtualFiles(ffmpeg: FFmpeg, files: string[]) {
  await Promise.all(
    files.map(async (file) => {
      try {
        await ffmpeg.deleteFile(file);
      } catch {
        // The in-memory file is absent, which is fine between renders.
      }
    }),
  );
}

async function toFfmpegWasmBlobURL(url: string) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Could not load FFmpeg WebAssembly (${response.status}).`);
  }

  let wasmBuffer = await response.arrayBuffer();
  if (isGzipBuffer(wasmBuffer)) {
    wasmBuffer = await decompressGzipBuffer(wasmBuffer);
  }

  if (!isWasmBuffer(wasmBuffer)) {
    throw new Error('FFmpeg WebAssembly asset is not a valid wasm file.');
  }

  return URL.createObjectURL(new Blob([wasmBuffer], { type: 'application/wasm' }));
}

function isGzipBuffer(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer, 0, Math.min(buffer.byteLength, 2));
  return bytes[0] === 0x1f && bytes[1] === 0x8b;
}

function isWasmBuffer(buffer: ArrayBuffer) {
  const bytes = new Uint8Array(buffer, 0, Math.min(buffer.byteLength, 4));
  return bytes[0] === 0x00 && bytes[1] === 0x61 && bytes[2] === 0x73 && bytes[3] === 0x6d;
}

async function decompressGzipBuffer(buffer: ArrayBuffer) {
  if (typeof DecompressionStream === 'undefined') {
    throw new Error('This browser cannot decompress the FFmpeg runtime. Try the latest Chrome, Edge, or Safari.');
  }

  const stream = new Blob([buffer]).stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(stream).arrayBuffer();
}
